import React, { useEffect, useState } from 'react';
import {
  Check,
  CreditCard,
  FileText,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { apiClient } from '../services/api-client';
import type {
  InvoiceDto,
  OrganizationUsageDto,
  PlanDto,
} from '@antigravity/types';

export const BillingPage: React.FC = () => {
  const [usage, setUsage] = useState<OrganizationUsageDto | null>(null);
  const [plans, setPlans] = useState<PlanDto[]>([]);
  const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscribingCode, setSubscribingCode] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usageData, plansData, invoicesData] = await Promise.all([
        apiClient.getUsage(),
        apiClient.getPlans(),
        apiClient.getInvoices(),
      ]);
      setUsage(usageData);
      setPlans(plansData);
      setInvoices(invoicesData);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to load billing metrics' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSubscribe = async (planCode: string) => {
    try {
      setSubscribingCode(planCode);
      setMessage(null);
      await apiClient.subscribe(planCode, billingInterval);
      setMessage({
        type: 'success',
        text: `Successfully updated subscription to ${planCode} plan (${billingInterval.toLowerCase()}).`,
      });
      await loadData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Subscription change failed' });
    } finally {
      setSubscribingCode(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription at the end of the current period?')) {
      return;
    }
    try {
      setLoading(true);
      await apiClient.cancelSubscription(false, 'Cancelled via web dashboard');
      setMessage({
        type: 'success',
        text: 'Subscription set to cancel at the end of the billing period.',
      });
      await loadData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to cancel subscription' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !usage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-slate-400">
        <Loader2 className="w-8 h-8 animate-spin text-brand-400 mb-3" />
        <p className="text-sm">Loading billing details and tenant quotas...</p>
      </div>
    );
  }

  const sub = usage?.subscription;
  const currentPlan = usage?.plan;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <CreditCard className="w-6 h-6 text-brand-400" />
            <span>Subscription & Plan Quotas</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Manage your workspace subscription, monitor real-time resource quotas, and review billing invoices.
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 hover:border-slate-700 text-xs font-semibold text-slate-300 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Alert banner */}
      {message && (
        <div
          className={`p-4 rounded-xl text-sm border flex items-center justify-between ${
            message.type === 'success'
              ? 'bg-emerald-950/40 border-emerald-500/30 text-emerald-300'
              : 'bg-red-950/40 border-red-500/30 text-red-300'
          }`}
        >
          <span>{message.text}</span>
          <button
            onClick={() => setMessage(null)}
            className="text-xs font-mono opacity-70 hover:opacity-100"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Subscription Status Card */}
      <div className="p-6 rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900/90 to-slate-950 border border-slate-800 shadow-xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-xs uppercase tracking-wider font-bold text-slate-400">
                Current Plan
              </span>
              <span
                className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                  sub?.status === 'ACTIVE'
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : sub?.status === 'TRIALING'
                    ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                    : sub?.status === 'PAST_DUE'
                    ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}
              >
                {sub?.status}
              </span>
              {sub?.cancelAtPeriodEnd && (
                <span className="px-2 py-0.5 rounded text-[10px] bg-amber-500/10 text-amber-300 border border-amber-500/20">
                  Cancels at period end
                </span>
              )}
            </div>
            <h2 className="text-3xl font-extrabold text-white tracking-tight">
              {currentPlan?.name || 'Free Tier'}
            </h2>
            <p className="text-xs text-slate-400">
              Period ends:{' '}
              <span className="text-slate-300 font-mono">
                {sub?.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString() : 'N/A'}
              </span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            {sub?.status === 'ACTIVE' && !sub?.cancelAtPeriodEnd && (
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-red-400 hover:bg-red-500/10 border border-slate-800 transition-colors"
              >
                Cancel Subscription
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Real-Time Tenant Quotas Grid */}
      <div>
        <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
          <Zap className="w-4 h-4 text-brand-400" />
          <span>Tenant Quotas & Consumption</span>
        </h3>

        {usage && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {Object.entries(usage.quotas).map(([key, q]) => {
              const pct = Math.min(100, Math.round((q.used / Math.max(1, q.limit)) * 100));
              const isDanger = pct >= 85;
              const isOver = q.isExceeded;

              return (
                <div
                  key={key}
                  className={`p-4 rounded-xl bg-slate-900/60 border transition-all ${
                    isOver
                      ? 'border-red-500/40 bg-red-950/10'
                      : isDanger
                      ? 'border-amber-500/40 bg-amber-950/10'
                      : 'border-slate-800'
                  }`}
                >
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                    <span className="capitalize font-semibold text-slate-300">{q.resource}</span>
                    <span className="font-mono text-[11px]">
                      {q.used} / {q.limit}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        isOver
                          ? 'bg-red-500'
                          : isDanger
                          ? 'bg-amber-500'
                          : 'bg-brand-500'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
                    <span>{q.remaining} remaining</span>
                    <span className="font-mono">{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Available Plans & Upgrades */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-brand-400" />
              <span>Available Subscription Plans</span>
            </h3>
            <p className="text-xs text-slate-400">
              Upgrade your workspace to scale sending volume and unlock advanced infrastructure limits.
            </p>
          </div>

          {/* Monthly / Yearly Toggle */}
          <div className="inline-flex items-center p-1 rounded-xl bg-slate-900 border border-slate-800">
            <button
              onClick={() => setBillingInterval('MONTHLY')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                billingInterval === 'MONTHLY'
                  ? 'bg-brand-600 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('YEARLY')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                billingInterval === 'YEARLY'
                  ? 'bg-brand-600 text-white'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Yearly <span className="text-[10px] text-emerald-400 font-bold ml-1">20% off</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {plans.map((p) => {
            const isCurrent = currentPlan?.code === p.code;
            const price =
              billingInterval === 'YEARLY'
                ? Math.round(p.priceYearlyCents / 100 / 12)
                : Math.round(p.priceMonthlyCents / 100);

            return (
              <div
                key={p.id}
                className={`p-6 rounded-2xl flex flex-col justify-between border transition-all ${
                  isCurrent
                    ? 'bg-brand-950/20 border-brand-500 shadow-xl shadow-brand-500/10'
                    : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-white text-base">{p.name}</h4>
                    {isCurrent && (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-brand-500/20 text-brand-300">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mb-4">{p.description}</p>

                  <div className="mb-6">
                    <span className="text-3xl font-extrabold text-white">${price}</span>
                    <span className="text-xs text-slate-400"> / month</span>
                  </div>

                  <ul className="space-y-2 text-xs text-slate-300 mb-6 border-t border-slate-800/60 pt-4">
                    <li className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-brand-400" />
                      <span>{p.maxDomains} sending domains</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-brand-400" />
                      <span>{p.maxMailboxes} mailboxes</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-brand-400" />
                      <span>{p.maxMonthlyEmails.toLocaleString()} emails / mo</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-brand-400" />
                      <span>{p.maxApiKeys} API keys</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-brand-400" />
                      <span>{p.maxMembers} workspace members</span>
                    </li>
                  </ul>
                </div>

                <button
                  onClick={() => handleSubscribe(p.code)}
                  disabled={isCurrent || subscribingCode === p.code}
                  className={`w-full py-2.5 rounded-xl text-xs font-semibold transition-all ${
                    isCurrent
                      ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                      : 'bg-brand-600 hover:bg-brand-500 text-white shadow-lg shadow-brand-600/25'
                  }`}
                >
                  {subscribingCode === p.code ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : isCurrent ? (
                    'Current Plan'
                  ) : (
                    'Select Plan'
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Invoice History */}
      <div className="space-y-4">
        <h3 className="text-base font-bold text-white flex items-center gap-2">
          <FileText className="w-4 h-4 text-brand-400" />
          <span>Billing History & Invoices</span>
        </h3>

        {invoices.length === 0 ? (
          <div className="p-8 rounded-xl bg-slate-900/40 border border-slate-800 text-center text-xs text-slate-400">
            No invoices generated yet for this workspace.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-800 overflow-hidden bg-slate-900/40">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-950 text-slate-400 uppercase tracking-wider text-[10px] border-b border-slate-800">
                <tr>
                  <th className="px-4 py-3">Invoice Number</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {invoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-800/30">
                    <td className="px-4 py-3 font-mono font-medium text-white">{inv.invoiceNumber}</td>
                    <td className="px-4 py-3 text-slate-400">
                      {new Date(inv.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 capitalize text-slate-300">
                      {inv.billingReason?.replace(/_/g, ' ').toLowerCase() || 'Subscription'}
                    </td>
                    <td className="px-4 py-3 font-semibold text-white">
                      ${(inv.amountCents / 100).toFixed(2)} {inv.currency}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        {inv.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
